import { ComponentType, ReactNode } from 'react';
import type { WidgetType, SpecificWidgetConfig, DataFlowCategory } from './types';
import { FALLBACK_WIDGET_ICON } from './widgetIcons';
// Widget imports are auto-generated — run: bash scripts/generate-widget-registry.sh
import { definitions } from './registry.generated';

/** A configurable parameter on a data-provider widget */
export interface DataProviderParam {
  /** Key sent to the data-provider (e.g. "horizon", "index") */
  key: string;
  /** Human-readable label (e.g. "Horizon", "Comparison Index") */
  label: string;
  /** Default value used when not overridden */
  defaultValue: string | number;
  /** Unit label shown next to the input (e.g. "trading days", "ticker") */
  unit?: string;
  /** Input type hint */
  type?: 'text' | 'number' | 'select';
  /** Options for 'select' type — { value, label } pairs */
  options?: Array<{ value: string; label: string }>;
  /** Placeholder text */
  placeholder?: string;
}

// Standard interface every widget exports from its index.ts
export interface WidgetDefinition {
  type: WidgetType;
  meta: {
    label: string;
    icon: ReactNode;
    description: string;
    isSystem: boolean;
    isCreatable: boolean;
    shareable?: boolean;      // Widget stores user data and can be shared/referenced across dashboards
    /** Widget has a config editor in the workflow builder sidebar (e.g. edit questions, pick file) */
    editableInWorkflow?: boolean;
    /** Widget requires an asset (e.g. ticker/UID) to fetch data. Workflows containing
     *  any asset-requiring widget are automatically marked as requiring an asset. */
    requiresAsset?: boolean;
    /** Data flow category: display-only, user-input, or both. Defaults to 'display'. */
    dataFlow?: DataFlowCategory;
    /** Whether this widget type can be designated as the summary widget for a dashboard. */
    summaryEligible?: boolean;
    /** Frontend-defined date string (YYYY-MM-DD) indicating when the widget was last updated.
     *  Used to signal "new" or "recently updated" to users. */
    lastUpdated?: string;
  };
  /** Custom tags for filtering/discovery on the Widgets page.
   *  Auto-derived tags (from category, isSystem, dataProvider) are added by catalog.ts.
   *  Use this for domain-specific tags like 'financial', 'real-time', etc. */
  tags?: string[];
  /** Declares which data-provider widget(s) this frontend widget consumes.
   *  When set, the widget fetches data via POST /api/datasources/widgets.
   *  Use the `useDataProviderWidget` hook in the renderer to fetch. */
  dataProvider?: {
    /** Data-provider widget name (e.g. "PRICE", "COMPANY_EARNINGS").
     *  This is the key sent in the `widgets` array to the data-provider service. */
    widget: string;
    /** Parameters that can be configured per workflow step.
     *  Each param declares its default, label, unit, and type. */
    params?: DataProviderParam[];
  };
  defaultSize: {
    w: number;
    h: number;
    minW: number;
    minH: number;
  };
  defaultConfig: () => SpecificWidgetConfig;
  Builder: ComponentType<any>;
  Renderer: ComponentType<any>;
  /** Static illustration shown in the widget preview modal.
   *  Should be a self-contained component with no API calls or external deps. */
  Preview?: ComponentType;
  /** Compact read-only view rendered in Summary page table cells.
   *  Receives the widget's stored data and config overrides. */
  Summary?: ComponentType<{ data: unknown; config: Record<string, unknown> }>;
  /** Sample data values to demonstrate the Summary component in the widget preview modal. */
  sampleSummaryData?: unknown[];
  /** Validate widget data before allowing step completion in workflows.
   *  Called with the widget's stored dashboardData entry.
   *  Return an array of error strings (empty = valid). */
  validate?: (data: unknown) => string[];
}

// Central registry map: WidgetType → WidgetDefinition
export const widgetRegistry = new Map<WidgetType, WidgetDefinition>(
  definitions.map(def => [def.type, def])
);

/** Check if a widget type requires an asset (ticker/UID) to function */
export function widgetRequiresAsset(type: string): boolean {
  const def = widgetRegistry.get(type as WidgetType);
  return def?.meta.requiresAsset === true;
}

/** Get the display label for a widget type. Single source of truth — do NOT duplicate this. */
export function getWidgetLabel(type: string): string {
  const def = widgetRegistry.get(type as WidgetType);
  return def?.meta.label ?? type;
}

// Derived constants (replaces hardcoded arrays in WidgetBuilderModal)
export const ALL_WIDGET_DEFS = definitions;
export const CREATABLE_WIDGET_DEFS = definitions.filter(d => d.meta.isCreatable);
export const SHAREABLE_WIDGET_TYPES = new Set(
  definitions.filter(d => d.meta.shareable).map(d => d.type)
);
export const SUMMARY_ELIGIBLE_TYPES = new Set(
  definitions.filter(d => d.meta.summaryEligible).map(d => d.type)
);

// Derived from each widget's meta.dataFlow (replaces hardcoded record in types.ts)
export const WIDGET_DATA_FLOW: Record<string, DataFlowCategory> = Object.fromEntries(
  definitions.map(d => [d.type, d.meta.dataFlow ?? 'display'])
) as Record<WidgetType, DataFlowCategory>;

/** Returns true if the widget type requires user input (input or both). */
export const isInputWidget = (type: WidgetType): boolean =>
  WIDGET_DATA_FLOW[type] === 'input' || WIDGET_DATA_FLOW[type] === 'both';

/** Get the icon for a widget type. Falls back to a generic icon for unknown types. */
export function getWidgetIcon(type: string): ReactNode {
  const def = widgetRegistry.get(type as WidgetType);
  return def?.meta.icon ?? FALLBACK_WIDGET_ICON;
}
